import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { InviteResultStatus } from '@gg/shared';
import { MAX_INVITES_PER_BATCH } from '@gg/shared';
import { Button, Notice, PlusIcon, StickyFooter, TextField } from '@/components/ui';
import { funnel } from '@/lib/analytics';
import { ApiRequestError } from '@/lib/api';
import { clearInviteDraft, readInviteDraft, type InviteDraftKey } from '@/lib/inviteDraft';
import { useSendInvites } from '@/lib/queries';
import { sentIfAllFilledSent, splitInviteFields } from './sendable';

type FormValues = { emails: { value: string }[] };

/** Error codes of POST /me/referral/invites that have their own message. */
const KNOWN_ERRORS = ['rate_limited', 'not_participant', 'unauthorized'];

/**
 * Email fields + "Add another" + one submit (P-07, screens 04 and 04b). The submit is enabled
 * as soon as one field holds a valid address (decision 0006); filled fields that are not an
 * email are flagged and not sent. When every filled address was sent, the participant goes to
 * the profile, which confirms how many left (decision 0007). Otherwise they stay: each refused
 * address shows its reason, sent ones are locked so the next submit only carries the rest.
 *
 * `ready` is false while the confirmation screen is still waiting for the session: the fields
 * can be filled, the submit waits and `waitingNote` says why. `blocked` replaces the submit.
 * With `onHold` (no session to send with), the submit keeps the addresses instead.
 */
export function InviteForm({
  fields: initialFields,
  ready = true,
  blocked,
  waitingNote,
  secondaryAction,
  draftKey,
  onHold,
}: {
  fields: number;
  ready?: boolean;
  blocked?: ReactNode;
  waitingNote?: ReactNode;
  secondaryAction?: ReactNode;
  /** Pre-fills the fields with addresses kept from the confirmation screen; cleared once sent. */
  draftKey?: InviteDraftKey;
  /** No session yet (record 0027, R1): the submit hands over the valid addresses, sends nothing. */
  onHold?: (emails: string[]) => void;
}) {
  const { t } = useTranslation();
  const send = useSendInvites();
  const navigate = useNavigate();
  const [results, setResults] = useState<Record<string, InviteResultStatus>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const [draft] = useState(() => readInviteDraft(draftKey).slice(0, MAX_INVITES_PER_BATCH));
  const form = useForm<FormValues>({
    defaultValues: {
      emails: Array.from({ length: Math.max(initialFields, draft.length) }, (_, i) => ({
        value: draft[i] ?? '',
      })),
    },
  });
  const { fields, append } = useFieldArray({ control: form.control, name: 'emails' });
  const values = form.watch('emails');
  const withValues = (emails: { value: string }[]) =>
    fields.map((f, i) => ({ id: f.id, value: emails[i]?.value ?? '' }));
  const canSubmit = splitInviteFields(withValues(values), results).send.length > 0;

  const onSubmit = form.handleSubmit(async ({ emails }) => {
    setFormError(null);
    const { send: pending, invalid } = splitInviteFields(withValues(emails), results);
    if (invalid.length > 0) {
      setResults((prev) => ({
        ...prev,
        ...Object.fromEntries(invalid.map((id) => [id, 'invalid' as const])),
      }));
    }
    if (pending.length === 0) return;
    if (onHold) return onHold(pending.map((p) => p.value));
    try {
      const out = await send.mutateAsync({ emails: pending.map((p) => p.value) });
      clearInviteDraft(draftKey);
      const next: typeof results = {
        ...results,
        ...Object.fromEntries(invalid.map((id) => [id, 'invalid' as const])),
      };
      out.results.forEach((r, i) => {
        const field = pending[i];
        if (field) next[field.id] = r.status;
      });
      setResults(next);
      const sent = out.results.filter((r) => r.status === 'sent').length;
      if (sent > 0) funnel.inviteSent(sent);
      const allSent = sentIfAllFilledSent(withValues(emails), next);
      if (allSent !== null) navigate('/me', { state: { invitesSent: allSent } });
    } catch (err) {
      const code = err instanceof ApiRequestError ? err.code : '';
      setFormError(KNOWN_ERRORS.includes(code) ? t(`invite.error.${code}`) : t('error.generic'));
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-1 flex-col gap-4">
      <div className="flex flex-col gap-2">
        {fields.map((field, i) => {
          const status = results[field.id];
          const n = i + 1;
          return (
            <TextField
              key={field.id}
              type="email"
              inputMode="email"
              autoComplete="off"
              aria-label={t('invite.fieldLabel', { n })}
              placeholder={t('invite.fieldPlaceholder', { n })}
              readOnly={status === 'sent'}
              hint={status === 'sent' ? t('invite.result.sent') : undefined}
              hintTone="success"
              error={status && status !== 'sent' ? t(`invite.result.${status}`) : undefined}
              {...form.register(`emails.${i}.value`, {
                // Editing a rejected address clears its reason.
                onChange: () => {
                  if (!status || status === 'sent') return;
                  setResults((prev) => {
                    const next = { ...prev };
                    delete next[field.id];
                    return next;
                  });
                },
              })}
            />
          );
        })}
        {fields.length < MAX_INVITES_PER_BATCH && (
          <button
            type="button"
            onClick={() => append({ value: '' })}
            className="flex h-10 items-center gap-1.5 self-start px-1 text-sm font-semibold text-accent hover:text-accent-hover"
          >
            <PlusIcon size={16} />
            {t('invite.add')}
          </button>
        )}
      </div>

      <Notice>{t('invite.notice')}</Notice>

      <StickyFooter>
        {formError && (
          <p role="alert" className="m-0 text-center text-helper text-danger">
            {formError}
          </p>
        )}
        {blocked ?? (
          <>
            <Button
              type="submit"
              loading={send.isPending}
              disabled={(!ready && !onHold) || !canSubmit}
            >
              {t('invite.send')}
            </Button>
            {!ready && !onHold && waitingNote && (
              <p role="status" className="m-0 text-center text-helper text-ink-muted">
                {waitingNote}
              </p>
            )}
          </>
        )}
        {secondaryAction}
      </StickyFooter>
    </form>
  );
}
